import { createSelector } from 'reselect';
import { FIELD_TYPES } from '@atlassian/jira-polaris-domain-field/src/field-types/index.tsx';
import type { FieldKey } from '@atlassian/jira-polaris-domain-field/src/field/types.tsx';
import { cacheSelectorCreator } from '@atlassian/jira-polaris-lib-selector-creator-cache/src/index.tsx';
import type { LocalIssueId } from '@atlassian/jira-polaris-domain-idea/src/idea/types.tsx';
import type { Props, State } from '../types.tsx';
import { createGetFieldMapping } from './fields.tsx';
import { getRankedIssueIds, getAllIssueIdsConsideringArchived } from './issue-ids.tsx';

export type GroupIdMap<TValueType> = Record<
	LocalIssueId,
	{
		groupIdentity: string;
		value: TValueType;
	}[]
>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const EMPTY_IDENTITIES: Record<string, any> = {};

export const createGetGroupIdentities =
	(fieldKey?: FieldKey) =>
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	(state: State, props: Props | undefined): GroupIdMap<any> => {
		const mapping = createGetFieldMapping(fieldKey)(state, props);
		if (fieldKey === undefined || mapping === undefined) {
			return EMPTY_IDENTITIES;
		}
		const cached = cacheSelectorCreator(mapping.getGroupIdentitiesSelector);

		if (mapping.field?.type === FIELD_TYPES.CONNECTION) {
			return cached(fieldKey, getAllIssueIdsConsideringArchived)(state, props);
		}

		return cached(fieldKey, getRankedIssueIds)(state);
	};

type IdentityValue = {
	fieldKey: FieldKey;
	fieldValue: unknown;
};

type Identity = {
	group?: IdentityValue;
	column?: IdentityValue;
};

export const createGetIssueGroupIdentities = ({
	localIssueId,
	groupBy,
	verticalGroupBy,
}: {
	localIssueId: LocalIssueId;
	groupBy: FieldKey | undefined;
	verticalGroupBy: FieldKey | undefined;
}) =>
	createSelector(
		createGetGroupIdentities(groupBy),
		createGetGroupIdentities(verticalGroupBy),
		(groupIdentities, verticalGroupIdentities) => {
			const issueVerticalGroupIdentities = verticalGroupIdentities[localIssueId]?.length
				? verticalGroupIdentities[localIssueId]
				: [{ value: undefined }];
			const issueGroupIdentities = groupIdentities[localIssueId]?.length
				? groupIdentities[localIssueId]
				: [{ value: undefined }];
			const identities: Identity[] = [];

			issueVerticalGroupIdentities.forEach((verticalIdentity) => {
				issueGroupIdentities.forEach((groupIdentity) => {
					identities.push({
						group: verticalGroupBy
							? {
									fieldKey: verticalGroupBy,
									fieldValue: verticalIdentity.value,
								}
							: undefined,
						column: groupBy
							? {
									fieldKey: groupBy,
									fieldValue: groupIdentity.value,
								}
							: undefined,
					});
				});
			});

			return identities;
		},
	);
